import { Injectable, OnDestroy } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { BehaviorSubject, Observable, Subscription, defer } from 'rxjs';
import { filter, finalize, take, takeUntil, tap } from 'rxjs/operators';
import { ConfigDTO } from '../api/models/configDTO.model';
import { UpdateConfigRequestDTO } from 'app/core/api/models/updateConfigRequestDTO.model';
import { UpdateInviteEmailDTO } from '../api/models/updateInviteEmailDTO.model';
import { ConfigApiService } from '../api/services/config-api.service';
import { DefaultConfig } from './constants/default-config.constant';
import { OrganizationContextService } from '../auth/organization-context.service';

@Injectable({
  providedIn: 'root',
})
export class ConfigService implements OnDestroy {
  private defaultOrganizationConfig: ConfigDTO = DefaultConfig;
  private organizationConfig: ConfigDTO = null;

  private readonly config$: BehaviorSubject<ConfigDTO> = new BehaviorSubject<ConfigDTO>(null);
  private readonly contextSubscription: Subscription;
  private request: Subscription | null = null;
  private generation = -1;
  private requestFailed = false;

  constructor(
    private readonly configApiService: ConfigApiService,
    private readonly context: OrganizationContextService,
    private readonly msal: MsalService,
  ) {
    // Legacy persistence has no tenant/account identity and cannot be trusted on restore.
    localStorage.removeItem('user-config');
    this.contextSubscription = context.state$.subscribe((state) => {
      if (state.generation !== this.generation) {
        this.generation = state.generation;
        this.request?.unsubscribe();
        this.request = null;
        this.organizationConfig = null;
        this.requestFailed = false;
        this.config$.next(null);
      }
      if (state.phase === 'ready' && this.config$.observed) this.loadConfiguration();
    });
  }

  get loadFailed(): boolean {
    return this.requestFailed || ['failed', 'stale'].includes(this.context.snapshot.phase);
  }

  get requiresSessionReload(): boolean {
    return ['failed', 'stale'].includes(this.context.snapshot.phase);
  }

  /**
   * Get the organization config.
   * If the organization config has already been loaded, the internal copy is returned.
   * Only the current confirmed context's in-memory copy may be replayed.
   * A restored session fetches configuration from the authenticated API.
   *
   * @returns An observable for the organization config
   */
  getOrganizationConfig(): Observable<ConfigDTO> {
    this.context.restoreSession(this.msal.instance.getAllAccounts(), this.msal.instance.getActiveAccount());
    this.loadConfiguration(true);
    return this.config$;
  }

  /**
   * Update the organization config.
   * The organization config is updated via the API and the internal copy is updated.
   *
   * @param updateConfigRequest The update config request
   * @returns An observable for the updated organization config
   */
  updateOrganizationConfig(updateConfigRequest: UpdateConfigRequestDTO): Observable<ConfigDTO> {
    return defer(() => {
      this.context.assertRequestAllowed();
      if (this.context.snapshot.phase !== 'ready') throw new Error('Organization context is not ready');
      const generation = this.context.snapshot.generation;
      return this.configApiService.updateOrganizationConfig(updateConfigRequest).pipe(
        takeUntil(this.context.invalidated$),
        filter(() => this.isCurrent(generation)),
        tap((config) => this.publish(config)),
      );
    });
  }

  isOrganizationConfigAvailable(): boolean {
    return this.organizationConfig !== null;
  }

  // Getters
  getLanguageAbbreviation(): string {
    return localStorage.getItem('ClientLanguage') ?? this.organizationConfig?.languageAbbreviation ?? 'de';
  }

  getOrganizationId(): string {
    return this.organizationConfig?.organizationId ?? this.defaultOrganizationConfig.organizationId;
  }

  getLicenseKey(): string {
    return this.organizationConfig?.licenseKey ?? this.defaultOrganizationConfig.licenseKey;
  }

  getLicenseEdition(): string {
    return this.organizationConfig?.licenseEdition ?? this.defaultOrganizationConfig.licenseEdition;
  }

  getAvailableUserRoles(): string[] {
    return this.organizationConfig?.availableUserRoles ?? this.defaultOrganizationConfig.availableUserRoles;
  }

  // Feature Flags
  //////////////////////////////////////////////////////////////////////////
  isDepartmentsDynamic(): boolean {
    return this.organizationConfig?.isDepartmentsDynamic ?? this.defaultOrganizationConfig.isDepartmentsDynamic;
  }

  isUserPortalEnabled(): boolean {
    return this.organizationConfig?.isUserPortalEnabled ?? this.defaultOrganizationConfig.isUserPortalEnabled;
  }

  isMonitoringEnabled(): boolean {
    return this.organizationConfig?.isMonitoringEnabled ?? this.defaultOrganizationConfig.isMonitoringEnabled;
  }

  isCostControlApiEnabled(): boolean {
    return this.organizationConfig?.isCostControlApiEnabled ?? this.defaultOrganizationConfig.isCostControlApiEnabled;
  }

  isCostControlUiEnabled(): boolean {
    return this.organizationConfig?.isCostControlUiEnabled ?? this.defaultOrganizationConfig.isCostControlUiEnabled;
  }

  isUserEmailNotificationOnInviteEnabled(): boolean {
    return (
      this.organizationConfig?.isUserEmailNotificationOnInviteEnabled ??
      this.defaultOrganizationConfig.isUserEmailNotificationOnInviteEnabled
    );
  }

  isAdminEmailNotificationOnInviteEnabled(): boolean {
    return (
      this.organizationConfig?.isAdminEmailNotificationOnInviteEnabled ??
      this.defaultOrganizationConfig.isAdminEmailNotificationOnInviteEnabled
    );
  }

  isUserPortalCommunityTileEnabled(): boolean {
    return (
      this.organizationConfig?.isUserPortalCommunityTileEnabled ??
      this.defaultOrganizationConfig.isUserPortalCommunityTileEnabled
    );
  }

  isAdminPortalCommunityTileEnabled(): boolean {
    return (
      this.organizationConfig?.isAdminPortalCommunityTileEnabled ??
      this.defaultOrganizationConfig.isAdminPortalCommunityTileEnabled
    );
  }

  // Configurable properties
  getUserPortalDepartmentNameSingular(): string {
    return (
      this.organizationConfig?.userPortalDepartmentNameSingular ??
      this.defaultOrganizationConfig.userPortalDepartmentNameSingular
    );
  }

  getUserPortalDepartmentNamePlural(): string {
    return (
      this.organizationConfig?.userPortalDepartmentNamePlural ??
      this.defaultOrganizationConfig.userPortalDepartmentNamePlural
    );
  }

  getUserPortalUserNameSingular(): string {
    return (
      this.organizationConfig?.userPortalUserNameSingular ?? this.defaultOrganizationConfig.userPortalUserNameSingular
    );
  }

  getUserPortalUserNamePlural(): string {
    return this.organizationConfig?.userPortalUserNamePlural ?? this.defaultOrganizationConfig.userPortalUserNamePlural;
  }

  getAdminPortalDepartmentNameSingular(): string {
    return (
      this.organizationConfig?.adminPortalDepartmentNameSingular ??
      this.defaultOrganizationConfig.adminPortalDepartmentNameSingular
    );
  }

  getAdminPortalDepartmentNamePlural(): string {
    return (
      this.organizationConfig?.adminPortalDepartmentNamePlural ??
      this.defaultOrganizationConfig.adminPortalDepartmentNamePlural
    );
  }

  getAdminPortalUserNameSingular(): string {
    return (
      this.organizationConfig?.adminPortalUserNameSingular ?? this.defaultOrganizationConfig.adminPortalUserNameSingular
    );
  }

  getAdminPortalUserNamePlural(): string {
    return (
      this.organizationConfig?.adminPortalUserNamePlural ?? this.defaultOrganizationConfig.adminPortalUserNamePlural
    );
  }

  // Invite E-Mail Content
  //////////////////////////////////////////////////////////////////////////
  getEmailSubjectLine(): string {
    return this.organizationConfig?.emailSubjectLine ?? this.defaultOrganizationConfig.emailSubjectLine;
  }

  getUserInviteEmailContent(): Observable<UpdateInviteEmailDTO> {
    return this.configApiService.getUserInviteEmailContent();
  }

  updateUserInviteEmailContent(inviteEmailContent: UpdateInviteEmailDTO): Observable<void> {
    return this.configApiService.updateUserInviteEmailContent(inviteEmailContent);
  }

  getAdminInviteEmailContent(): Observable<UpdateInviteEmailDTO> {
    return this.configApiService.getAdminInviteEmailContent();
  }

  updateAdminInviteEmailContent(inviteEmailContent: UpdateInviteEmailDTO): Observable<void> {
    return this.configApiService.updateAdminInviteEmailContent(inviteEmailContent);
  }

  // Cost Control
  getCostControlAvailableAiModels(): Map<string, string[]> {
    this.getOrganizationConfig()
      .pipe(
        filter((cfg) => !!cfg),
        take(1),
      )
      .subscribe();
    return (
      this.organizationConfig?.costControlAvailableAiModels ??
      this.defaultOrganizationConfig.costControlAvailableAiModels
    );
  }

  // Themes
  getLightTheme(): string {
    return this.organizationConfig?.lightTheme ?? this.defaultOrganizationConfig.lightTheme;
  }

  getDarkTheme(): string {
    return this.organizationConfig?.darkTheme ?? this.defaultOrganizationConfig.darkTheme;
  }

  private isCurrent(generation: number): boolean {
    try {
      this.context.assertRequestAllowed();
    } catch {
      return false;
    }
    return this.context.snapshot.phase === 'ready' && this.context.snapshot.generation === generation;
  }

  private publish(config: ConfigDTO): void {
    this.organizationConfig = config;
    this.config$.next(config);
  }

  private loadConfiguration(refresh = false): void {
    if (this.request || (this.organizationConfig && !refresh) || this.context.snapshot.phase !== 'ready') return;
    let account;
    try {
      account = this.context.resolveAccount(this.msal.instance.getAllAccounts(), this.msal.instance.getActiveAccount());
    } catch {
      return;
    }
    const roles = ((account?.idTokenClaims?.roles as string[]) ?? []).map((role) => role.toLowerCase());
    // The InstanceAdmin shell has no organization configuration endpoint/entitlement.
    if (!roles.some((role) => role === 'admin' || role === 'user')) return;
    const generation = this.context.snapshot.generation;
    const pending = new Subscription();
    this.request = pending;
    this.requestFailed = false;
    const current = this.organizationConfig;
    pending.add(
      defer(() =>
        current ? this.configApiService.checkUserConfiguration(current) : this.configApiService.getOrganizationConfig(),
      )
        .pipe(
          filter(() => this.isCurrent(generation)),
          finalize(() => {
            if (this.request === pending) this.request = null;
          }),
        )
        .subscribe({
          next: (config) => this.publish(config),
          error: () => {
            if (this.isCurrent(generation)) {
              this.requestFailed = true;
              this.config$.next(this.organizationConfig);
            }
          },
        }),
    );
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
    this.contextSubscription.unsubscribe();
    this.config$.complete();
  }
}
